/**
 * Location Transfer Service - API methods for center/location transfer operations
 */

import apiClient from './apiClient';
import type { PaginatedResponse } from './locationService';

export interface TransferHistoryEntry {
  id: string;
  entityType: 'clinician' | 'client';
  entityId: string;
  entityName: string;
  fromCenterName: string;
  toCenterName: string;
  reason: string;
  initiatedByName: string;
  effectiveFrom: string;
  createdAt: string;
}

export interface CaseloadByCenter {
  centerId: string;
  centerName: string;
  clients: { id: string; firstName: string; lastName: string }[];
}

export interface TransferClinicianRequest {
  memberId: string;
  toCenterId: string;
  reason: string;
}

export interface TransferClinicianResponse {
  transfer: any;
  caseload: CaseloadByCenter[];
}

export interface TransferClientRequest {
  clientId: string;
  toCenterId: string;
  reason: string;
  bcbaDecision?: { action: 'keep' | 'reassign'; newBcbaId?: string };
  rbtDecisions?: { rbtMemberId: string; action: 'keep' | 'remove' }[];
}

export interface BulkTransferRequest {
  fromCenterId: string;
  toCenterId: string;
  reason: string;
  entityType: 'clinician' | 'client' | 'all';
}

export interface CenterScope {
  isOrgWide: boolean;
  centerIds: string[];
}

export const transferClinician = async (
  data: TransferClinicianRequest,
): Promise<TransferClinicianResponse> => {
  const response = await apiClient.post<{ success: boolean; data: TransferClinicianResponse }>(
    '/api/v1/locations/transfers/clinician',
    data,
  );
  return response.data.data;
};

export const transferClient = async (
  data: TransferClientRequest,
): Promise<{ transfer: any }> => {
  const response = await apiClient.post<{ success: boolean; data: { transfer: any } }>(
    '/api/v1/locations/transfers/client',
    data,
  );
  return response.data.data;
};

export const bulkTransfer = async (
  data: BulkTransferRequest,
): Promise<{ clinicianTransfers: number; clientTransfers: number }> => {
  const response = await apiClient.post<{
    success: boolean;
    data: { clinicianTransfers: number; clientTransfers: number };
  }>('/api/v1/locations/transfers/bulk', data);
  return response.data.data;
};

export const getTransferHistory = async (
  centerId: string,
  params: { page?: number; limit?: number; type?: 'clinician' | 'client' },
): Promise<PaginatedResponse<TransferHistoryEntry>> => {
  const response = await apiClient.get<
    { success: boolean } & PaginatedResponse<TransferHistoryEntry>
  >(`/api/v1/locations/${centerId}/transfer-history`, { params });
  return { data: response.data.data, meta: response.data.meta };
};

export const getClinicianCaseload = async (
  memberId: string,
): Promise<{ caseload: CaseloadByCenter[] }> => {
  const response = await apiClient.get<{ success: boolean; data: { caseload: CaseloadByCenter[] } }>(
    `/api/v1/locations/members/${memberId}/caseload`,
  );
  return response.data.data;
};

export const getMemberScope = async (memberId: string): Promise<CenterScope> => {
  const response = await apiClient.get<{ success: boolean; data: CenterScope }>(
    `/api/v1/locations/members/${memberId}/scope`,
  );
  return response.data.data;
};

export const setMemberScope = async (
  memberId: string,
  data: { isOrgWide: boolean; centerIds?: string[] },
): Promise<CenterScope> => {
  const response = await apiClient.put<{ success: boolean; data: CenterScope }>(
    `/api/v1/locations/members/${memberId}/scope`,
    data,
  );
  return response.data.data;
};

const locationTransferService = {
  transferClinician,
  transferClient,
  bulkTransfer,
  getTransferHistory,
  getClinicianCaseload,
  getMemberScope,
  setMemberScope,
};

export default locationTransferService;
